/**
 * Planning tab of an event: its phases (EventItemPhase) and the lines of each phase
 * (EventItemLine: menus, products, services) with quantity, price, discount and total. The
 * sum of the lines is the event total (VenueEvent.totalAmount), shown in the Info tab too.
 * Phases are added from the toolbar "+" (EventDetailViewController -> addPhase).
 */
class EventDetailPlanningViewController extends MUIViewController
{
    private phasesLabel:MUILabel = null;
    private paxLabel:MUILabel    = null;
    private totalLabel:MUILabel  = null;
    private tableView:UITableView = null;

    private nf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.integerFormatter;
    private cf:MIONumberFormatter = MUIWebApplication.sharedInstance().delegate.currencyFormatter;

    viewDidLoad(){
        super.viewDidLoad();

        this.phasesLabel = MUIOutlet(this, 'phases-lbl', 'MUILabel');
        this.paxLabel = MUIOutlet(this, 'pax-lbl', 'MUILabel');
        this.totalLabel = MUIOutlet(this, 'total-lbl', 'MUILabel');

        this.tableView = MUIOutlet(this, 'table-view', 'UITableView');
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);
        this.updateUI();
    }

    private _event:VenueEvent = null;
    set event(value:VenueEvent){
        this._event = value;
        this._fetchedResultsController = null;
        this.updateUI();
    }

    private updateUI(){
        if (this.viewIsLoaded == false || this._event == null) return;

        this.paxLabel.text = this.nf.stringFromNumber(this._event.pax || 0);
        this.rebuildDisplayList();
        this.tableView.reloadData();
        this.recalculateEventTotal();
    }

    // --- Display list mapping ---
    // Each entry is either { type:'item', frcIndex:number } or { type:'add', phase:EventItemPhase }
    private _displayList:any[] = [];

    private rebuildDisplayList(){
        let list = [];
        let phases = 0;
        let objects = this.fetchedResultsController?.fetchedObjects;
        if (objects == null) { this._displayList = list; return; }

        let currentPhase:EventItemPhase = null;
        for (let i = 0; i < objects.length; i++){
            let item = objects[i] as EventItem;

            // When we hit a new phase, insert an add-cell for the previous phase (if any)
            if (item instanceof EventItemPhase){
                phases++;
                if (currentPhase != null){
                    list.push({ type: 'add', phase: currentPhase });
                }
                currentPhase = item as EventItemPhase;
            }

            list.push({ type: 'item', frcIndex: i });
        }

        // Add cell for the last phase
        if (currentPhase != null){
            list.push({ type: 'add', phase: currentPhase });
        }

        this._displayList = list;
        if (this.phasesLabel != null) this.phasesLabel.text = this.nf.stringFromNumber(phases);
    }

    // --- Table View Data Source ---

    numberOfSections(tableview){
        return 1;
    }

    numberOfRowsInSection(tableview, section){
        return this._displayList.length;
    }

    cellAtIndexPath(tableview:UITableView, indexPath:MIOIndexPath){
        let entry = this._displayList[indexPath.row];

        if (entry.type === 'add'){
            let cell:EventDetailAddCell = tableview.dequeueReusableCellWithIdentifier('EventItemAddCell') as EventDetailAddCell;
            cell.phase = entry.phase;
            cell.delegate = this;
            return cell;
        }

        let item = this.fetchedResultsController.fetchedObjects[entry.frcIndex] as EventItem;
        let cellIdentifier = (item instanceof EventItemPhase) ? 'EventItemPhaseCell' : 'EventItemLineCell';
        let cell:EventDetailCell = tableview.dequeueReusableCellWithIdentifier(cellIdentifier) as EventDetailCell;
        cell.delegate = this;
        cell.item = item;
        return cell;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let entry = this._displayList[indexPath.row];
        if (entry.type === 'add') return;
        let item = this.fetchedResultsController.fetchedObjects[entry.frcIndex] as EventItem;
        if (item instanceof EventItemLine) {
            let line = item as EventItemLine;
            if (line.menu != null) {
                let vc = MenuDetailViewController.newInstance();
                vc.item = line.menu;
                this.navigationController.pushViewController(vc, true);
            }
        }
    }

    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let entry = this._displayList[indexPath.row];
        if (entry.type === 'add') return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView:UITableView, editingStyle:UITableViewCellEditingStyle, indexPath:MIOIndexPath){
        if (editingStyle != UITableViewCellEditingStyle.Delete) return;
        let entry = this._displayList[indexPath.row];
        if (entry.type === 'add') return;
        let item = this.fetchedResultsController.fetchedObjects[entry.frcIndex] as EventItem;
        DBHelper.deleteObjectFromMainContext(item, true);
    }

    // --- Line cell delegate ---

    lineCellDidUpdateTotal(cell:EventDetailCell){
        this.recalculateEventTotal();
    }

    // --- Add cell delegate ---

    addCellDidEnter(cell:EventDetailAddCell, phase:EventItemPhase, concept:string, type:number){
        this.createEventItemLine(phase, concept, null, null);
    }

    addCellDidSelectProduct(cell:EventDetailAddCell, phase:EventItemPhase, product:Product){
        this.createEventItemLine(phase, product.name, product, null);
    }

    addCellDidSelectMenu(cell:EventDetailAddCell, phase:EventItemPhase, menu:Menu){
        this.createEventItemLine(phase, menu.name, null, menu);
    }

    private createEventItemLine(phase:EventItemPhase, concept:string, product:Product, menu:Menu){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let moc = ad.managedObjectContext;

        let line = MIOEntityDescription.insertNewObjectForEntityForName("EventItemLine", moc) as EventItemLine;
        line.identifier = MIOUUID.UUID().UUIDString;
        line.concept = concept;
        line.orderIndex = phase.orderIndex + (phase.lines != null ? phase.lines.count : 0) + 1;
        line.event = this._event;
        line.phase = phase;

        if (menu != null) {
            line.menu = menu;
            line.unitPrice = menu.price || 0;            
        } else if (product != null) {
            line.product = product;
            line.unitPrice = product.price || 0;
        }

        line.quantity = this._event.pax || 1;
        line.totalAmount = (line.quantity || 0) * (line.unitPrice || 0);

        moc.save();
    }

    // --- FRC for EventItem ---

    private _fetchedResultsController:MIOFetchedResultsController = null;

    get fetchedResultsController(){
        if (this._fetchedResultsController != null)
            return this._fetchedResultsController;

        if (this._event == null) return null;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey('orderIndex', true)];
        let predicateFormat = "event.identifier == '" + this._event.identifier + "'";

        let fetchRequest = DBHelper.listFetchRequestWithEntityName('EventItem', sortDescriptors, predicateFormat);

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, "orderIndex");
        fetchedResultsController.delegate = this;
        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller){
        this.rebuildDisplayList();
        this.tableView.reloadData();
        this.recalculateEventTotal();
    }

    private recalculateEventTotal(){
        let total = 0;
        let objects = this.fetchedResultsController?.fetchedObjects;
        if (objects != null) {
            for (let i = 0; i < objects.length; i++){
                let item = objects[i] as EventItem;
                if (item instanceof EventItemLine) {
                    total += (item as EventItemLine).totalAmount || 0;
                }
            }
        }
        this._event.totalAmount = total;
        this.totalLabel.text = this.cf.stringFromNumber(total);
    }

    // --- Add Phase (toolbar "+") ---

    addPhase(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("ADD_PHASE", "ADD PHASE"), MIOLocalizeString("ENTER_PHASE_NAME", "ENTER PHASE NAME"), MUIAlertViewStyle.Default);

        avc.addTextFieldWithConfigurationHandler(this, function(textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("NAME", "NAME"));
        });

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD", "ADD"), MUIAlertActionStyle.Default, this, function(this:EventDetailPlanningViewController){
            let name = avc.textFields[0].text?.trim();
            if (name == null || name.length == 0) return;

            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let moc = ad.managedObjectContext;
            let phase = MIOEntityDescription.insertNewObjectForEntityForName("EventItemPhase", moc) as EventItemPhase;
            phase.identifier = MIOUUID.UUID().UUIDString;
            phase.concept = name;
            phase.orderIndex = this.fetchedResultsController.fetchedObjects.length;
            phase.event = this._event;

            moc.save();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }
}
